import { Injectable, NgZone, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import * as LDClient from 'launchdarkly-js-client-sdk';
import {
  FeatureFlagsServiceActions,
  FeatureFlagsFallbackActions,
} from '../../store/feature-flags/feature-flags.actions';
import envConfig from '../../env-config';
import { FEATURE_FLAGS } from '../tokens/feature-flags.token';
import {
  getAllFlagKeys,
  getFlagConfig,
  LaunchDarklyFlagKey,
  FeatureFlagConfig,
  isValidFlagKey,
} from '../constants/feature-flags.constants';

/**
 * Service for managing LaunchDarkly client lifecycle and flag synchronization
 * Initializes client, subscribes to flag changes, and syncs with NgRx store
 *
 * Note: This is a root-level service that lives for the entire application lifetime.
 * Cleanup in ngOnDestroy is not triggered, but included for completeness if the
 * service is ever used in a different scope (e.g., feature module or testing).
 */
@Injectable({ providedIn: 'root' })
export class FeatureFlagsService {
  private readonly featureFlags = inject(FEATURE_FLAGS);
  private readonly store = inject(Store);
  private readonly ngZone = inject(NgZone);

  private client?: LDClient.LDClient;
  private readonly clientReady: Promise<void>;
  private resolveClientReady!: () => void;
  private readonly changeListeners = new Map<
    LaunchDarklyFlagKey,
    (value: unknown) => void
  >();

  /** Default timeout for LaunchDarkly initialization (milliseconds) */
  private readonly DEFAULT_INIT_TIMEOUT_MS = 5000;

  constructor() {
    this.clientReady = new Promise<void>(resolve => {
      this.resolveClientReady = resolve;
    });
  }

  /**
   * Initialize LaunchDarkly client or fallback to static configuration
   * Called during application bootstrap via APP_INITIALIZER
   */
  public async initialize(): Promise<void> {
    this.store.dispatch(FeatureFlagsServiceActions.init());

    const ldConfig = envConfig.launchDarkly;

    // Validate clientSideId before attempting to initialize
    const isValidClientId =
      ldConfig?.clientSideId &&
      ldConfig.clientSideId.trim() !== '' &&
      !ldConfig.clientSideId.includes('${'); // Check for unsubstituted placeholders

    // Fallback to static defaults if LaunchDarkly is disabled or invalid
    if (!ldConfig?.enabled || !isValidClientId) {
      this.loadStaticDefaults();
      this.resolveClientReady();
      return;
    }

    try {
      await this.initializeLaunchDarklyClient(ldConfig);
      this.resolveClientReady();
    } catch (error) {
      console.error(
        '[FeatureFlags] LaunchDarkly initialization failed:',
        error
      );
      this.store.dispatch(
        FeatureFlagsServiceActions.initFailed({
          error: error instanceof Error ? error.message : 'Unknown error',
        })
      );
      this.loadStaticDefaults();
      this.resolveClientReady();
    }
  }

  /**
   * Initialize LaunchDarkly client with timeout protection
   */
  private async initializeLaunchDarklyClient(
    ldConfig: NonNullable<typeof envConfig.launchDarkly>
  ): Promise<void> {
    // Simple anonymous application context
    // Environment targeting is handled by which client-side ID is used:
    // - Dev uses fpsapiv2--launchdarkly-client-id_dev → LaunchDarkly Development env
    // - QA uses fpsapiv2--launchdarkly-client-id_qa → LaunchDarkly Customer Test env
    // - Prod uses fpsapiv2--launchdarkly-client-id_prod → LaunchDarkly Production env
    const context: LDClient.LDContext = {
      kind: 'application',
      key: 'fps-web-v2',
      anonymous: true,
    };

    this.client = LDClient.initialize(ldConfig.clientSideId, context);

    // Set up event listeners BEFORE waiting for ready
    this.setupClientEventListeners();

    // Wait for ready with timeout
    await this.waitForClientReady();
  }

  /**
   * Build LaunchDarkly deployment context for environment targeting
   * Uses UKG standard pattern: kind: "deployment" with region and environment attributes
   */
  /** Set up LaunchDarkly client event listeners */
  private setupClientEventListeners(): void {
    if (!this.client) return;

    // Ready event
    this.client.on('ready', () => {
      this.ngZone.run(() => {
        this.store.dispatch(FeatureFlagsServiceActions.ready());
      });
    });

    // Failed event
    this.client.on('failed', (error: Error) => {
      this.ngZone.run(() => {
        this.store.dispatch(
          FeatureFlagsServiceActions.initFailed({
            error: error.message,
          })
        );
        // Fall back to defaults on permanent failure
        this.loadStaticDefaults();
      });
    });

    // Subscribe to all configured flags
    this.subscribeToAllFlags();
  }

  /**
   * Wait for LaunchDarkly client to be ready with timeout
   */
  private async waitForClientReady(): Promise<void> {
    if (!this.client) {
      throw new Error('Client not initialized');
    }

    // Use configurable timeout or default
    const timeoutMs =
      envConfig.launchDarkly?.initTimeout ?? this.DEFAULT_INIT_TIMEOUT_MS;

    return new Promise<void>((resolve, reject) => {
      let settled = false; // Guard flag to prevent race condition

      const timeout = setTimeout(() => {
        if (!settled) {
          settled = true;
          reject(new Error('LaunchDarkly initialization timeout'));
        }
      }, timeoutMs);

      this.client!.waitForInitialization()
        .then(() => {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            resolve();
          }
        })
        .catch((error: Error) => {
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            reject(error);
          }
        });
    });
  }

  /** Subscribe to all configured feature flags */
  private subscribeToAllFlags(): void {
    if (!this.client) return;

    const flagKeys = getAllFlagKeys();

    for (const key of flagKeys) {
      const config = getFlagConfig(key);
      this.subscribeToFlag(key, config);
    }
  }

  /** Subscribe to a single feature flag change event */
  private subscribeToFlag(
    key: LaunchDarklyFlagKey,
    config: FeatureFlagConfig
  ): void {
    if (!this.client) return;

    const listener = (newValue: unknown) => {
      this.ngZone.run(() => {
        // Type-safe value conversion
        const typedValue = this.convertToType(newValue, config);

        this.store.dispatch(
          FeatureFlagsServiceActions.updateFlag({
            key,
            domain: config.domain,
            property: config.property,
            value: typedValue,
          })
        );
      });
    };

    // Store listener for cleanup
    this.changeListeners.set(key, listener);

    // Subscribe to LaunchDarkly change event
    this.client.on(`change:${key}`, listener);
  }

  /** Convert LaunchDarkly value to boolean with fallback */
  private convertToType(value: unknown, config: FeatureFlagConfig): boolean {
    // All feature flags are boolean-only (ON/OFF toggles)
    if (typeof value === 'boolean') {
      return value;
    }

    return config.defaultValue;
  }

  /** Load static default values when LaunchDarkly is unavailable */
  private loadStaticDefaults(): void {
    this.store.dispatch(
      FeatureFlagsFallbackActions.loadFromDefaults({
        flags: this.featureFlags,
      })
    );
  }

  public async identifyUser(user: {
    id: number | string;
    email: string;
  }): Promise<void> {
    await this.clientReady;

    if (!this.client || !user.email) {
      return;
    }

    await this.client.identify({
      kind: 'multi',
      application: {
        key: 'fps-web-v2',
        anonymous: true,
      },
      user: {
        key: user.email,
        email: user.email,
      },
    });
  }

  /**
   * Get the current value of a toggle with type conversion
   * @param toggleName - LaunchDarkly flag key
   * @param defaultValue - Fallback value if client unavailable
   * @returns Typed value matching flag configuration or default
   */
  public getToggleValue(toggleName: string, defaultValue?: unknown): unknown {
    if (!this.client) {
      return defaultValue;
    }

    const rawValue = this.client.variation(toggleName, defaultValue);

    // Apply type conversion if flag is in our configuration
    if (isValidFlagKey(toggleName)) {
      const config = getFlagConfig(toggleName as LaunchDarklyFlagKey);
      return this.convertToType(rawValue, config);
    }

    return rawValue;
  }
}
